import { useState } from 'react';
import { Alert, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

import {
  faBan,
  faCog,
  faFolder,
  faInfoCircle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Camera } from 'src/models/Camera.model';

import useUiTranslation from 'src/hooks/useUiTranslation';

interface IImageSelectorColumnProps {
  isClient: boolean;
  imageFolder: string | null;
  camera: Camera;
  newFiles: string[];
  leftUrl: string;
  rightUrl: string;
  onImageSelect: (imageUrl: string, eye: string | undefined) => void;
}

const ImageSelectorColumn = ({
  isClient,
  imageFolder,
  camera,
  newFiles,
  leftUrl,
  rightUrl,
  onImageSelect,
}: IImageSelectorColumnProps) => {
  const { tPages } = useUiTranslation();
  const history = useHistory();

  const initialTouchDragState = {
    imageUrl: '',
    imageWidth: 0,
    initialImagePosition: { x: 0, y: 0 },
    initialTouchPosition: { x: 0, y: 0 },
    distanceDragged: { x: 0, y: 0 },
  };

  const [touchDragState, setTouchDragState] = useState<{
    imageUrl: string;
    imageWidth: number;
    initialImagePosition: { x: number; y: number };
    initialTouchPosition: { x: number; y: number };
    distanceDragged: { x: number; y: number };
  }>(initialTouchDragState);

  const handleTouchDrag = (event: React.TouchEvent<HTMLElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLImageElement)) return;

    const touch = event.touches[0];
    setTouchDragState((prev) => {
      if (!prev.imageUrl) {
        const image = target.getBoundingClientRect();
        return {
          imageUrl: target.src,
          imageWidth: target.width,
          initialImagePosition: { x: image.left, y: image.top },
          initialTouchPosition: { x: touch.clientX, y: touch.clientY },
          distanceDragged: { x: 0, y: 0 },
        };
      }

      return {
        ...prev,
        distanceDragged: {
          x: touch.clientX - prev.initialTouchPosition.x,
          y: touch.clientY - prev.initialTouchPosition.y,
        },
      };
    });
  };

  const handleTouchDragEnd = (event: React.TouchEvent<HTMLElement>) => {
    const imageUrl = touchDragState.imageUrl;
    if (!imageUrl) return;

    const touch = event.changedTouches[0];
    const targetId = document.elementFromPoint(
      touch.clientX,
      touch.clientY,
    )?.id;

    onImageSelect(imageUrl, targetId);

    setTouchDragState(initialTouchDragState);
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {touchDragState.imageUrl && (
        <img
          src={touchDragState.imageUrl}
          alt=""
          className="position-absolute z-3 pe-none"
          style={{
            width: touchDragState.imageWidth,
            left: touchDragState.initialImagePosition.x,
            top: touchDragState.initialImagePosition.y,
            transform: `translate(${touchDragState.distanceDragged.x}px, ${touchDragState.distanceDragged.y}px)`,
          }}
        />
      )}
      <div
        className="text-center align-middle"
        style={{ width: '100%', height: '100%' }}
      >
        {isClient && imageFolder ? (
          <Card
            className="text-start"
            style={{ height: '100%', overflowY: 'auto' }}
          >
            <Card.Header>
              <FontAwesomeIcon icon={faFolder} className="me-2" />
              {imageFolder}
            </Card.Header>

            <Card.Body className="ps-0 pe-3 pt-0 pb-3">
              <Container fluid>
                <Row>
                  {newFiles.map((file) => (
                    <Col sm={6} className="ps-3 pe-0 pt-3 pb-0" key={file}>
                      <img
                        src={new URL(file).href}
                        alt=""
                        className={
                          leftUrl === new URL(file).href ||
                          rightUrl === new URL(file).href
                            ? 'border border-primary border-5 img-fluid'
                            : 'border border-dashed border-5 img-fluid'
                        }
                        draggable="true"
                        onTouchMove={handleTouchDrag}
                        onTouchEnd={handleTouchDragEnd}
                        style={{ touchAction: 'none' }}
                      />
                    </Col>
                  ))}
                  {newFiles.length === 0 && (
                    <p className="text-center p-4">
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      {tPages('newExam.waitingForNewFundusImages')}
                    </p>
                  )}
                </Row>
              </Container>
            </Card.Body>
            <Card.Footer className="text-muted">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              <i>{tPages('newExam.dragAndDropImages')}</i>
            </Card.Footer>
          </Card>
        ) : (
          <Alert variant="warning" className="p-4">
            <h3>
              <FontAwesomeIcon icon={faBan} className="me-2" />
            </h3>

            <p className="mt-4">
              {camera === Camera.SMALL
                ? tPages('newExam.cameraNotConnected')
                : tPages('newExam.fileMonitoringDisabled')}{' '}
              {tPages('newExam.useManualUpload')}
            </p>

            {isClient && (
              <Button
                variant="outline-secondary"
                className="mt-3"
                onClick={() => {
                  history.push({
                    pathname: '/settings',
                    state: { isManualFileUpload: true },
                  });
                }}
              >
                <FontAwesomeIcon icon={faCog} className="me-2" />
                {tPages('newExam.setupImageFolder')}
              </Button>
            )}
          </Alert>
        )}
      </div>
    </div>
  );
};

export default ImageSelectorColumn;
